import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'

type AuthorizationDetail = {
  id: number
  client: number
  client_name: string
  insurance_coverage: number
  payer_name: string
  authorization_number: string
  service_code: string
  start_date: string
  end_date: string
  authorized_units: string
  used_units: string
  pending_units: string
  available_units: string
  status: string
  is_active: boolean
  requires_supervision: boolean
  supervision_ratio: string | null
  place_of_service_codes: string[]
  modifiers_required: string[]
  created_at: string
  updated_at: string
}

type LedgerEntry = {
  id: number
  entry_type: string
  units: string
  units_before: string
  units_after: string
  session: number | null
  notes: string | null
  created_by_name: string
  created_at: string
}

export default function AuthorizationDetailPage() {
  const tenantSlug = resolveTenantSlug()
  const params = useParams()
  const authorizationId = Number(params.authorizationId)

  const detailCacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:authorizations:detail:${authorizationId}`
  }, [authorizationId, tenantSlug])

  const ledgerCacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:authorizations:ledger:${authorizationId}`
  }, [authorizationId, tenantSlug])

  const detailQuery = useOfflineFirstQuery<AuthorizationDetail>({
    queryKey: ['authorization', authorizationId],
    cacheKey: detailCacheKey,
    cacheMaxAgeMs: 10 * 60 * 1000,
    enabled: Number.isFinite(authorizationId) && authorizationId > 0,
    queryFn: async () => {
      const res = await apiClient.get<AuthorizationDetail>(`/api/v1/authorizations/${authorizationId}/`)
      return res.data
    },
  })

  const ledgerQuery = useOfflineFirstQuery<LedgerEntry[]>({
    queryKey: ['authorization', authorizationId, 'ledger'],
    cacheKey: ledgerCacheKey,
    cacheMaxAgeMs: 5 * 60 * 1000,
    enabled: Number.isFinite(authorizationId) && authorizationId > 0,
    queryFn: async () => {
      const res = await apiClient.get<LedgerEntry[]>(`/api/v1/authorizations/${authorizationId}/ledger/`)
      return res.data
    },
  })

  if (!Number.isFinite(authorizationId) || authorizationId <= 0) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Invalid authorization id.
      </div>
    )
  }

  const auth = detailQuery.data
  const ledger = ledgerQuery.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Authorization</h1>
          <p className="text-sm text-gray-600">
            {auth ? (
              <>
                <Link to={`/app/clients/${auth.client}`} className="font-medium text-primary-600 hover:text-primary-700">
                  {auth.client_name}
                </Link>{' '}
                | {auth.authorization_number} | {auth.service_code}
              </>
            ) : (
              'Loading authorization...'
            )}
          </p>
        </div>
        <Link to="/app/authorizations" className="btn-secondary">
          Back
        </Link>
      </div>

      {(detailQuery.isError || ledgerQuery.isError) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(detailQuery.error) || getErrorMessage(ledgerQuery.error)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
            {detailQuery.isLoading && !auth ? (
              <div className="space-y-3">
                <div className="h-4 w-96 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-80 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-72 bg-gray-200 rounded animate-pulse" />
              </div>
            ) : auth ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Payer</div>
                  <div className="text-gray-900">{auth.payer_name}</div>
                </div>
                <div>
                  <div className="text-gray-500">Status</div>
                  <div className="text-gray-900">{auth.status}</div>
                </div>
                <div>
                  <div className="text-gray-500">Dates</div>
                  <div className="text-gray-900">
                    {auth.start_date} - {auth.end_date}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Active</div>
                  <div className="text-gray-900">{auth.is_active ? 'Yes' : 'No'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Authorized units</div>
                  <div className="text-gray-900">{auth.authorized_units}</div>
                </div>
                <div>
                  <div className="text-gray-500">Used units</div>
                  <div className="text-gray-900">{auth.used_units}</div>
                </div>
                <div>
                  <div className="text-gray-500">Pending units</div>
                  <div className="text-gray-900">{auth.pending_units}</div>
                </div>
                <div>
                  <div className="text-gray-500">Available units</div>
                  <div className="text-gray-900">{auth.available_units}</div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-gray-500">Payer constraints</div>
                  <div className="text-gray-900">
                    POS: {(auth.place_of_service_codes ?? []).join(', ') || '-'} | Modifiers: {(auth.modifiers_required ?? []).join(', ') || '-'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">No authorization data.</div>
            )}
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Utilization Ledger</h2>
            {ledgerQuery.isLoading && ledger.length === 0 ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, idx) => (
                  <div key={idx} className="h-10 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            ) : ledger.length === 0 ? (
              <div className="text-sm text-gray-600">No ledger entries.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="table-header">When</th>
                      <th className="table-header">Type</th>
                      <th className="table-header">Units</th>
                      <th className="table-header">Balance After</th>
                      <th className="table-header">Session</th>
                      <th className="table-header">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {ledger.slice(0, 50).map((e) => (
                      <tr key={e.id} className="hover:bg-gray-50">
                        <td className="table-cell">{e.created_at}</td>
                        <td className="table-cell">{e.entry_type}</td>
                        <td className="table-cell">{e.units}</td>
                        <td className="table-cell">{e.units_after}</td>
                        <td className="table-cell">
                          {e.session ? (
                            <Link to={`/app/sessions/${e.session}`} className="text-primary-600 hover:text-primary-700">
                              {e.session}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="table-cell">{e.created_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Supervision</h2>
            {auth ? (
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Required</span>
                  <span className="font-medium text-gray-900">{auth.requires_supervision ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Ratio</span>
                  <span className="font-medium text-gray-900">{auth.supervision_ratio ?? '-'}</span>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">No supervision data.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

